import { useId } from 'react';
import { cn } from 'src/utils/cn';

interface TrueToggleProps {
  id?: string;
  label: string;
  variant?: 'default' | 'success' | 'error' | 'warning' | 'info';
  description?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  containerClassName?: string;
  toggleClassName?: string;
  className?: string;
}

export const TrueToggle = ({
  id,
  label,
  description = '',
  variant = 'default',
  checked,
  onChange,
  disabled = false,
  containerClassName = '',
  toggleClassName = '',
  className = '',
}: TrueToggleProps) => {
  const reactId = useId();
  const baseId = id ?? `toggle-${reactId}`;
  const labelId = `${baseId}-label`;
  const descriptionId = `${baseId}-description`;

  return (
    <div
      className={cn(
        'flex items-center justify-between gap-x-2',
        containerClassName,
      )}
    >
      <span className="flex grow flex-col">
        <label
          id={labelId}
          htmlFor={baseId}
          className="text-sm/6 font-medium text-gray-900"
        >
          {label}
        </label>
        {description !== '' && (
          <span id={descriptionId} className="text-sm text-gray-500">
            {description}
          </span>
        )}
      </span>
      <div
        className={cn(
          'group relative inline-flex w-11 shrink-0 rounded-full bg-gray-200 p-0.5 inset-ring inset-ring-gray-900/5 outline-offset-2 transition-colors duration-200 ease-in-out  has-focus-visible:outline-2',
          className,
          toggleClassName,
          {
            'cursor-not-allowed opacity-50': disabled,
            'cursor-pointer': !disabled,
            'outline-indigo-600 has-checked:bg-indigo-600':
              variant === 'default' && checked,
            'outline-red-600 has-checked:bg-red-600':
              variant === 'error' && checked,
            'outline-yellow-600 has-checked:bg-yellow-600':
              variant === 'warning' && checked,
            'outline-green-600 has-checked:bg-green-600':
              variant === 'success' && checked,
            'outline-blue-500 has-checked:bg-blue-500':
              variant === 'info' && checked,
            'has-focus-visible:outline-gray-300': !checked,
          },
        )}
      >
        <span className="relative size-5 rounded-full bg-white shadow-xs ring-1 ring-gray-900/5 transition-transform duration-200 ease-in-out group-has-checked:translate-x-5 cursor-pointer">
          <span
            aria-hidden="true"
            className="absolute inset-0 flex size-full items-center justify-center opacity-100 transition-opacity duration-200 ease-in group-has-checked:opacity-0 group-has-checked:duration-100 group-has-checked:ease-out"
          >
            <svg
              fill="none"
              viewBox="0 0 12 12"
              className="size-3 text-gray-400"
            >
              <path
                d="M4 8l2-2m0 0l2-2M6 6L4 4m2 2l2 2"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <span
            aria-hidden="true"
            className="absolute inset-0 flex size-full items-center justify-center opacity-0 transition-opacity duration-100 ease-out group-has-checked:opacity-100 group-has-checked:duration-200 group-has-checked:ease-in"
          >
            <svg
              fill="currentColor"
              viewBox="0 0 12 12"
              className={cn('size-3', {
                'text-red-600': variant === 'error' && checked,
                'text-yellow-600': variant === 'warning' && checked,
                'text-green-600': variant === 'success' && checked,
                'text-indigo-600': variant === 'default' && checked,
              })}
            >
              <path d="M3.707 5.293a1 1 0 00-1.414 1.414l1.414-1.414zM5 8l-.707.707a1 1 0 001.414 0L5 8zm4.707-3.293a1 1 0 00-1.414-1.414l1.414 1.414zm-7.414 2l2 2 1.414-1.414-2-2-1.414 1.414zm3.414 2l4-4-1.414-1.414-4 4 1.414 1.414z" />
            </svg>
          </span>
        </span>
        <input
          id={baseId}
          name={baseId}
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={(e) => {
            if (!disabled) {
              onChange(e.target.checked);
            }
          }}
          aria-labelledby={labelId}
          aria-describedby={description ? descriptionId : undefined}
          className="absolute inset-0 appearance-none focus:outline-hidden"
        />
      </div>
    </div>
  );
};
